import { ArrowLeft, UserPlus } from 'lucide-react'
import { useState } from 'react'
import { Helmet } from 'react-helmet'

import { UrgencyIndicator } from '@/components/quotes/UrgencyIndicator'
import { Button } from '@/components/ui/button'
import { Link } from '@/components/ui/Link'
import { ProductFlowNav } from '@/routes/Public/marketing/components/ProductFlowNav'
import { MATCHED_BROKER } from '../../navFixtures'
import { Cap } from '../../primitives'
import { PrototypeFrame } from '../../PrototypeFrame'
import { CarrierCheckoutCard } from './CarrierCheckoutCard'
import { CheckoutCartPanel } from './CheckoutCartPanel'
import { CHECKOUTS } from './checkoutData'
import { UpdateApplicationLink } from './UpdateApplicationLink'

// A count, not a date: a fixed date drifts past expiry and the countdown goes negative.
const DAYS_UNTIL_EXPIRATION = 18

const BRIEF = (
  <div className="border-ink-700 grid grid-cols-1 gap-6 border-t pt-6 sm:grid-cols-2 lg:grid-cols-4">
    <div>
      <Cap onDark>Goal</Cap>
      <p className="text-ink-200 mt-2 text-xs leading-relaxed">
        Shorten the time from adding coverages to the cart to coverage going active. It averages 3.9 days
        today.
      </p>
    </div>

    <div>
      <Cap onDark>Project: Sub-five self serve</Cap>
      <p className="text-ink-200 mt-2 text-xs leading-relaxed">
        Small fleets should be able to get covered without waiting on a broker to step in at every turn.
      </p>
    </div>

    <div>
      <Cap onDark>Feedback</Cap>
      <ul className="text-ink-200 mt-2 flex flex-col gap-1 text-xs leading-relaxed">
        <li>
          Slack channel:{' '}
          <a
            href="https://cailabsinc.slack.com/archives/C0C9Z0B7R1V"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-300 hover:text-brand-200 underline underline-offset-2"
          >
            #test-checkout-manager
          </a>
        </li>
      </ul>
    </div>

    <div>
      <Cap onDark>Next</Cap>
      <p className="text-ink-200 mt-2 text-xs leading-relaxed">
        Feedback on what this page collects and where it sits in the funnel. Is this everything we need to
        bind?
      </p>
    </div>
  </div>
)

export function CheckoutManagerPrototypePage() {
  const [selectedPlans, setSelectedPlans] = useState<Record<string, string>>({})
  // Removing a provider from the cart takes its checkout card with it.
  const [checkouts, setCheckouts] = useState(CHECKOUTS)

  return (
    <PrototypeFrame
      title="Checkout manager V2"
      owner="Carrie Phillips"
      date="October 19, 2026"
      status="Front-end prototype"
      brief={BRIEF}
      fullBleedContent
    >
      <Helmet>
        <title>Prototype: Checkout manager V2</title>
      </Helmet>
      <div className="bg-background flex min-h-screen flex-col">
        <ProductFlowNav
          hideLogin
          containerAligned
          disableScrollHide
          matchedBroker={MATCHED_BROKER}
          rightAction={
            // Same treatment as the nav's Share actions; mock, opens nothing yet.
            <Button type="button" variant="link" size="sm" className="gap-1.5">
              <UserPlus className="size-4" />
              Invite
            </Button>
          }
        />
        {/* Container's gutter, set by hand so the cart column can run to the viewport's right edge. */}
        <div className="flex flex-1 flex-col [--gutter:max(1rem,calc((100vw_-_80rem)/2_+_1rem))] lg:flex-row lg:items-start">
          <div className="mt-10 flex flex-col gap-6 px-4 pb-20 lg:flex-1 lg:pr-8 lg:pl-(--gutter)">
            <header className="flex flex-col gap-2">
              <Link
                kind="util"
                size="sm"
                icon={<ArrowLeft />}
                href="/design-system/prototypes/quotes-page"
                className="mb-4 self-start"
              >
                Go back to quotes
              </Link>
              <h1 className="text-ink text-3xl font-extrabold tracking-tight">Checkout manager</h1>
            </header>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <UpdateApplicationLink />
              <span className="text-ink-400 flex items-center gap-2 text-sm font-medium">
                <UrgencyIndicator daysRemaining={DAYS_UNTIL_EXPIRATION} />
                {DAYS_UNTIL_EXPIRATION} days until policy expiration
              </span>
            </div>
            {checkouts.map((checkout) => (
              <CarrierCheckoutCard
                key={checkout.quote.id}
                checkout={checkout}
                selectedPlan={selectedPlans[checkout.quote.id]}
                onSelectPlan={(title) =>
                  setSelectedPlans((current) => ({ ...current, [checkout.quote.id]: title }))
                }
              />
            ))}
          </div>
          <CheckoutCartPanel
            groups={checkouts.map((checkout) => checkout.quote)}
            selectedPlans={selectedPlans}
            onRemove={(id) =>
              setCheckouts((current) => current.filter((checkout) => checkout.quote.id !== id))
            }
          />
        </div>
      </div>
    </PrototypeFrame>
  )
}
